/**
 * WebSocket reconnection utility with exponential backoff.
 *
 * Shared between desktop (Electron) and PWA clients. Provides automatic
 * reconnection when the WebSocket connection drops, with exponential backoff
 * (1s, 2s, 4s, 8s... up to 30s max).
 */

import { RECONNECT_BASE_MS, RECONNECT_MAX_MS } from './constants.js';

export interface ReconnectOptions {
  /** Factory function that creates a new WebSocket connection. */
  connect: () => WebSocket;
  /** Called when the connection is successfully opened. */
  onOpen?: () => void;
  /** Called when a message is received. */
  onMessage?: (event: MessageEvent) => void;
  /** Called when the connection is closed. */
  onClose?: (event: CloseEvent) => void;
  /** Called when an error occurs. */
  onError?: (event: Event) => void;
  /** Called before each reconnection attempt with the delay in ms. */
  onReconnecting?: (attempt: number, delayMs: number) => void;
}

export interface ReconnectHandle {
  /** The current WebSocket instance. */
  ws: WebSocket;
  /** Stop reconnecting and close the current connection. */
  disconnect: () => void;
  /** Force an immediate reconnection (e.g., after coming back online). */
  reconnectNow: () => void;
}

/**
 * Calculate the reconnection delay with exponential backoff.
 * Base delay doubles each attempt, capped at RECONNECT_MAX_MS (30s).
 * Adds jitter (0-50% of base) to prevent thundering herd.
 */
export function getReconnectDelay(attempt: number): number {
  const base = Math.min(RECONNECT_BASE_MS * Math.pow(2, attempt), RECONNECT_MAX_MS);
  const jitter = base * 0.5 * Math.random();
  return base + jitter;
}

/**
 * Create a WebSocket connection with automatic reconnection.
 *
 * Usage:
 * ```ts
 * const handle = createReconnectingWebSocket({
 *   connect: () => new WebSocket(url),
 *   onOpen: () => { ws.send(JSON.stringify({ auth: jwt })); },
 *   onMessage: (e) => { handleMessage(JSON.parse(e.data)); },
 * });
 *
 * // Later:
 * handle.disconnect(); // Stop reconnecting
 * handle.reconnectNow(); // Force immediate reconnect
 * ```
 */
export function createReconnectingWebSocket(options: ReconnectOptions): ReconnectHandle {
  let ws: WebSocket;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let intentionalClose = false;

  function cleanup(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function connect(): void {
    intentionalClose = false;
    ws = options.connect();

    ws.onopen = () => {
      attempt = 0; // Reset backoff on successful connection
      options.onOpen?.();
    };

    ws.onmessage = (event) => {
      options.onMessage?.(event);
    };

    ws.onclose = (event) => {
      options.onClose?.(event);

      // Don't reconnect if we intentionally closed or if the close code
      // indicates a permanent failure (e.g., auth failure 4001)
      if (intentionalClose || event.code === 4001) {
        return;
      }

      scheduleReconnect();
    };

    ws.onerror = (event) => {
      options.onError?.(event);
      // onclose will fire after onerror, so reconnection is handled there
    };
  }

  function scheduleReconnect(): void {
    cleanup();
    const delay = getReconnectDelay(attempt);
    attempt++;
    options.onReconnecting?.(attempt, delay);

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  function disconnect(): void {
    intentionalClose = true;
    cleanup();
    if (ws && ws.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
      ws.close(1000, 'Client disconnect');
    }
  }

  function reconnectNow(): void {
    cleanup();
    if (ws) {
      intentionalClose = true;
      try { ws.close(1000, 'Reconnecting'); } catch { /* already closed */ }
    }
    intentionalClose = false;
    attempt = 0;
    connect();
  }

  // Initial connection
  connect();

  return {
    get ws() { return ws; },
    disconnect,
    reconnectNow,
  };
}
